import {
  spruceCallOf, spruceCallTitle, type SpruceCall, type SpruceCallTaskLike,
} from '../../../scripts/spruce-rows.mjs';
import {
  VISIT_KIND_NAME, VISIT_KIND_PERSON, VISIT_KIND_SHORT, visitKindOf,
} from './visits';
import type { VisitKind } from './visits';

export interface CompletionAttribution {
  /** Overrides the status verb ("Auto-completed"); null = keep the default "Completed". */
  verb: string | null;
  /** Muted "who/from" sub-line ("by care team"); null = none. */
  by: string | null;
}

// Tasks carry no per-user attribution — completion_source is the only "who" we have.
const COMPLETION_ATTRIBUTION: Record<string, CompletionAttribution> = {
  staff: { verb: null, by: 'by care team' },
  patient: { verb: null, by: 'by patient' },
  system: { verb: 'Auto-completed', by: null },
  device: { verb: 'Auto-completed', by: 'from device reading' },
  lab_feed: { verb: 'Auto-completed', by: 'from lab result' },
};

export function completionAttribution(source?: string | null): CompletionAttribution | null {
  if (!source) return null;
  return COMPLETION_ATTRIBUTION[source] ?? null;
}

// An answered clinic-line call WF84 kept as a task, read from its guarded context block and never from the title
// (WF84 and WF10 apply the same check: scripts/spruce-rows.mjs). Null for any other task.
export function clinicCallOf(task: SpruceCallTaskLike | null | undefined): SpruceCall | null {
  if (!task) return null;
  // View rows may nest the code under `.template`; the context's own template_code is never trusted here.
  const nested = (task as { template?: { template_code?: unknown } | null }).template?.template_code;
  return spruceCallOf({ ...task, template_code: task.template_code || nested });
}

export function isClinicCallTask(task: SpruceCallTaskLike | null | undefined): boolean {
  return clinicCallOf(task) !== null;
}

export function clinicCallLabel(call: SpruceCall): string {
  return spruceCallTitle(call.seconds, call.direction, call.teammate_firstname);
}

// What a task row shows as its title: a call's own label, else the stored title.
export function taskDisplayTitle(task: (SpruceCallTaskLike & { title?: string | null }) | null | undefined): string {
  const call = clinicCallOf(task);
  return call ? clinicCallLabel(call) : String(task?.title ?? '');
}

// The date a done task is filed under: a call's own time whatever its status (one rule on every surface: a cancel
// clears completed_at), else its completion or last change.
export function doneStampOf(task: (SpruceCallTaskLike & {
  completed_at?: string | null; modified?: string | null; updated_at?: string | null;
}) | null | undefined): string | null {
  if (!task) return null;
  const call = clinicCallOf(task);
  if (call) return call.at;
  return task.completed_at || task.modified || task.updated_at || null;
}

// WF17 refuses a cancel of a call task without a reason; the prefix tells the reader why it left the claim. An outgoing
// call is cancelled as not reaching the patient: Spruce marks a voicemail pickup answered.
export const NOT_THIS_PATIENT_PREFIX = 'Not this patient: ';
export const DID_NOT_REACH_PREFIX = 'Did not reach the patient: ';
export interface CallCancelCopy {
  action: string; prefix: string; tooltip: string; placeholder: string; hint: string; moveNote: string | null;
  toast: string; cancelledBy: string;
}
const CALL_CANCEL_COPY: Record<'inbound' | 'outbound', CallCancelCopy> = {
  inbound: {
    action: 'Not this patient',
    prefix: NOT_THIS_PATIENT_PREFIX,
    tooltip: 'This call was with someone else: it stops counting for this patient. A claim already created for its month is not changed.',
    placeholder: 'e.g. the caller was a neighbour, not the patient',
    hint: 'Kept on the task for your team; it never reaches the patient. Do not name the other person.',
    moveNote: 'The call cannot be moved to another patient here: record that patient\u2019s contact separately.',
    toast: 'Call marked not this patient. A claim already created for its month is unchanged: re-check it.',
    cancelledBy: 'not this patient',
  },
  outbound: {
    action: 'Did not reach the patient',
    prefix: DID_NOT_REACH_PREFIX,
    tooltip: 'This call did not reach the patient (the voicemail picked up, or someone else answered): it stops counting for this patient. A claim already created for its month is not changed.',
    placeholder: 'e.g. the voicemail picked up',
    hint: 'Kept on the task for your team; it never reaches the patient. If someone else answered, do not name them.',
    moveNote: null,
    toast: 'Call marked as not reaching the patient. A claim already created for its month is unchanged: re-check it.',
    cancelledBy: 'did not reach the patient',
  },
};
export function callCancelCopy(call: Pick<SpruceCall, 'direction'> | null | undefined): CallCancelCopy {
  return CALL_CANCEL_COPY[call?.direction === 'outbound' ? 'outbound' : 'inbound'];
}
export function notThisPatientNote(reason: string | null | undefined, call?: Pick<SpruceCall, 'direction'> | null): string | null {
  const r = String(reason ?? '').trim();
  return r ? callCancelCopy(call).prefix + r : null;
}

const CLOSED_STATUSES = new Set(['completed', 'cancelled', 'missed']);

interface AnchorableTask {
  status?: string;
  template_code?: string | null;
  context_json?: unknown;
  available_at?: string | null;
  due_at?: string | null;
  completed_at?: string | null;
  modified?: string | null;
  updated_at?: string | null;
  created?: string | null;
  created_at?: string | null;
}

// Which calendar day a task sits on. Closed tasks anchor on their close date, NOT
// available_at, so an onboarding batch sharing one enrollment-instant available_at
// doesn't collapse onto a single day.
export function anchorTaskDate(task: AnchorableTask): Date | null {
  const parse = (s: string) => {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  // A clinic-line call sits on the day it happened, whatever its task row says.
  const call = clinicCallOf(task as SpruceCallTaskLike);
  if (call) { const d = parse(call.at); if (d) return d; }
  if (task.status && CLOSED_STATUSES.has(task.status)) {
    const closedAt = task.completed_at ?? task.modified ?? task.updated_at;
    if (closedAt) { const d = parse(closedAt); if (d) return d; }
  }
  const actionable = task.available_at ?? task.due_at ?? task.completed_at
    ?? task.created ?? task.created_at;
  return actionable ? parse(actionable) : null;
}

// Exported so a sibling logic module reads a task's context_json the SAME way, rather than minting
// its own. ⚠️ careUpdate.ts still has a private copy that returns {} — not null — on bad JSON; its
// callers rely on that, so consolidating it is a behaviour change, not a rename.
export function readTaskContext(ctx: unknown): Record<string, unknown> | null {
  if (typeof ctx === 'string') {
    try { return JSON.parse(ctx); } catch { return null; }
  }
  if (ctx && typeof ctx === 'object') return ctx as Record<string, unknown>;
  return null;
}

/** Statuses in which a task is finished and no longer actionable. */
export function isClosedStatus(status: unknown): boolean {
  return CLOSED_STATUSES.has(String(status ?? ''));
}

interface TaskLike {
  task_id?: string | number;
  status?: string;
  owner_role?: string | null;
  context_json?: unknown;
  [key: string]: unknown;
}

export interface CompletionActor {
  user_id?: string;
  firstname?: string;
  lastname?: string;
  role?: string;
}

// Strip nullish fields; drop the wrapper entirely when it carries no identity.
export function cleanActorOf(actor?: CompletionActor): Record<string, unknown> | undefined {
  if (!actor || !(actor.firstname || actor.lastname || actor.user_id)) return undefined;
  return Object.fromEntries(Object.entries(actor).filter(([, v]) => v != null));
}

// Actor block for task_updated events, built from the session user. Audit label
// only — apiv2's session check is the real authz.
export function completionActorOf(
  currentUser: { accountid?: string | number; firstname?: string; lastname?: string; shortname?: string } | null | undefined,
  userRole?: string | null,
): { user_id?: string; firstname?: string; lastname?: string; role?: string } | undefined {
  if (!currentUser) return undefined;
  return {
    user_id: currentUser.accountid ? String(currentUser.accountid) : undefined,
    firstname: currentUser.firstname || currentUser.shortname || undefined,
    lastname: currentUser.lastname || undefined,
    role: userRole || undefined,
  };
}

// The note on the patient's lab card is signed with the user's first name, never the account short name (a handle).
export function labNoteActorOf(
  currentUser: { accountid?: string | number; firstname?: string; lastname?: string; shortname?: string } | null | undefined,
  userRole?: string | null,
): { user_id?: string; firstname?: string; lastname?: string; role?: string } | undefined {
  const actor = completionActorOf(currentUser, userRole);
  return actor && { ...actor, firstname: currentUser?.firstname || undefined };
}

// Canonical template-code reader — view rows nest `.template`, raw CRUD rows carry
// `template_code`, some only stamp context_json (object or string JSON).
export function templateCodeOf(
  task: { template?: { template_code?: string } | null; template_code?: string | null; context_json?: unknown } | null | undefined,
): string {
  if (!task) return '';
  return task.template?.template_code
    || task.template_code
    || (readTaskContext(task.context_json)?.template_code as string | undefined)
    || '';
}

interface PeriodScoped { id_care_period?: unknown; care_period_id?: unknown }

/** The task's care period id as a string, '' when the row does not say. */
export function taskCarePeriodId(task: PeriodScoped | null | undefined): string {
  const v = task?.id_care_period ?? task?.care_period_id;
  return v == null ? '' : String(v);
}

/** True when the row belongs to the period, or the row does not name one (older rows degrade to today's match). */
export function inCarePeriod(
  task: PeriodScoped | null | undefined,
  carePeriodId: string | number | null | undefined,
): boolean {
  if (carePeriodId == null || carePeriodId === '') return true;
  const own = taskCarePeriodId(task);
  return own === '' || own === String(carePeriodId);
}

/** Completion date (epoch ms) of the newest completed instance of a template on this
 *  patient, in the given care period when one is passed. Tri-state matching MilestoneBar's
 *  convention: number = done on that date; undefined = done but no usable date; null = not completed. */
export function completedTemplateAt(
  tasks: readonly (Parameters<typeof templateCodeOf>[0] & AnchorableTask & PeriodScoped)[] | undefined | null,
  templateCode: string,
  carePeriodId?: string | number | null,
): number | null | undefined {
  const done = (tasks || []).filter(
    (t) => t?.status === 'completed' && templateCodeOf(t) === templateCode && inCarePeriod(t, carePeriodId),
  );
  if (done.length === 0) return null;
  const times = done
    .map((t) => t.completed_at ?? t.modified ?? t.updated_at)
    .filter((s): s is string => !!s)
    .map((s) => new Date(s).getTime())
    .filter((n) => Number.isFinite(n));
  return times.length ? Math.max(...times) : undefined;
}

// Flip the just-completed task to completed in the patient-file bundle so Done updates before
// the async WF17 cascade lands. The bundle already holds every task (get_patient_file returns
// all statuses), so a plain map suffices — the poll reconciles with server truth.
export function applyOptimisticCompletion(
  bundle: TaskLike[] | null | undefined,
  taskId: string | number,
  actor: CompletionActor | undefined,
  nowIso: string,
): TaskLike[] {
  const idStr = String(taskId);
  return (bundle || []).map((t) => (String(t.task_id) === idStr
    ? {
      ...t,
      status: 'completed',
      completed_at: nowIso,
      completion_source: 'staff',
      context_json: {
        ...(readTaskContext(t.context_json) || {}),
        ...(actor ? { completed_by: { user_id: actor.user_id, firstname: actor.firstname, lastname: actor.lastname } } : {}),
      },
    }
    : t));
}

// Flip a just-closed task to cancelled in the patient-file bundle. No completed_at /
// completion_source (cancel ≠ completion); TaskCard's doneStamp falls back to `modified`.
export function applyOptimisticCancellation(
  bundle: TaskLike[] | null | undefined,
  taskId: string | number,
  nowIso: string,
  note?: string,
): TaskLike[] {
  const idStr = String(taskId);
  return (bundle || []).map((t) => (String(t.task_id) === idStr
    ? {
      ...t, status: 'cancelled', modified: nowIso, ...(note ? { completion_notes: note } : {}),
    }
    : t));
}

// WF17 stamps context_json.completed_by on staff completions; null → caller falls back to the source label.
export function completedByName(task: { context_json?: unknown } | null | undefined): string | null {
  const cb = readTaskContext(task?.context_json)?.completed_by as
    | { firstname?: string | null; lastname?: string | null }
    | undefined;
  if (cb && (cb.firstname || cb.lastname)) {
    return [cb.firstname, cb.lastname].filter(Boolean).join(' ');
  }
  return null;
}

export interface CompletionLine { verb: string; by: string | null; at: string | null }

// The completion line every task surface draws: a call by its own time and channel (never "Auto-completed"),
// any other completed task by who or what closed it. Null when there is nothing to draw.
export function completionLineOf(task: (SpruceCallTaskLike & {
  completion_source?: string | null; completed_at?: string | null;
}) | null | undefined): CompletionLine | null {
  if (!task) return null;
  const call = clinicCallOf(task);
  if (call) {
    if (task.status === 'completed') return { verb: 'Call', by: 'via the clinic line', at: call.at };
    if (task.status === 'cancelled') return { verb: 'Cancelled', by: callCancelCopy(call).cancelledBy, at: call.at };
    return null;
  }
  if (task.status !== 'completed') return null;
  const attr = completionAttribution(task.completion_source);
  const name = completedByName(task);
  return { verb: attr?.verb || 'Completed', by: name ? `by ${name}` : (attr?.by ?? null), at: task.completed_at ?? null };
}

// createTask stamps context_json.created_by on ad-hoc staff tasks; null → not a manual/attributed task.
export function createdByName(task: { context_json?: unknown } | null | undefined): string | null {
  const cb = readTaskContext(task?.context_json)?.created_by as
    | { firstname?: string | null; lastname?: string | null }
    | undefined;
  if (cb && (cb.firstname || cb.lastname)) {
    return [cb.firstname, cb.lastname].filter(Boolean).join(' ');
  }
  return null;
}

// Canonical priority rank — consolidating the private copies is a follow-up.
export const TASK_PRIORITY_RANK: Record<string, number> = {
  urgent: 4, high: 3, normal: 2, low: 1,
};

const UNSEQUENCED = 999;

// context_json.sequence = the care-team checklist slot (seeded in cadence_rule, stamped
// by WF6/WF7/WF12/WF17/WF52). Missing/invalid → 999 = "after the checklist, in due order".
export function taskSequence(task: { context_json?: unknown } | null | undefined): number {
  const raw = readTaskContext(task?.context_json)?.sequence;
  const n = Number(raw);
  return raw != null && Number.isFinite(n) && n > 0 ? n : UNSEQUENCED;
}

// WF52 stamps the intake's clinical flags on the reconcile task (late_* when it arrived
// after the task was closed). Both key families can coexist (enriched, closed, second late
// intake) — concatenate, marking late ones; never let `late_*` shadow or be shadowed.
export function intakeFlags(task: { context_json?: unknown } | null | undefined): string[] {
  const ctx = readTaskContext(task?.context_json);
  const pick = (raw: unknown): string[] => (Array.isArray(raw) ? raw.filter((f): f is string => typeof f === 'string') : []);
  const late = pick(ctx?.late_intake_flags).map((f) => `${f} (received after completion)`);
  return [...pick(ctx?.intake_flags), ...late];
}

interface OrderableTask {
  priority?: string | null;
  due_at?: string | null;
  context_json?: unknown;
}

// Patient-file open-task order: urgent first (safety override — WF9 escalations,
// Make-Blocking-Urgent), then the explicit checklist sequence, then priority, then due.
// Urgent-only first (not full priority-first) so low/normal sequenced tasks keep their slot.
export function compareOpenTasks(a: OrderableTask, b: OrderableTask): number {
  const urgentA = a.priority === 'urgent' ? 0 : 1;
  const urgentB = b.priority === 'urgent' ? 0 : 1;
  if (urgentA !== urgentB) return urgentA - urgentB;
  const seqA = taskSequence(a);
  const seqB = taskSequence(b);
  if (seqA !== seqB) return seqA - seqB;
  const prioA = TASK_PRIORITY_RANK[a.priority ?? ''] ?? 2; // unknown → normal
  const prioB = TASK_PRIORITY_RANK[b.priority ?? ''] ?? 2;
  if (prioA !== prioB) return prioB - prioA;
  const dueA = a.due_at ? new Date(a.due_at).getTime() : NaN;
  const dueB = b.due_at ? new Date(b.due_at).getTime() : NaN;
  const aBad = Number.isNaN(dueA);
  const bBad = Number.isNaN(dueB);
  if (aBad && bBad) return 0;
  if (aBad) return 1; // null/unparseable due → last
  if (bBad) return -1;
  return dueA - dueB;
}

export interface TaskSnooze {
  until: string;
  reason?: string;
  by?: { firstname?: string | null; lastname?: string | null } | null;
}

// A task is snoozed while today <= snooze.until (inclusive). Mirrors WF60/WF4 exactly, and
// fails OPEN for the same reason: an absent, malformed or expired `until` means NOT snoozed,
// so a bad value can never hide work. `exc_*` clinical alerts are never snoozable.
export function taskSnooze(
  task: Parameters<typeof templateCodeOf>[0],
): TaskSnooze | null {
  if (!task) return null;
  if (templateCodeOf(task).startsWith('exc_')) return null;
  let ctx: unknown = task.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch { return null; } }
  const s = (ctx as { snooze?: unknown } | null)?.snooze;
  if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
  const until = String((s as TaskSnooze).until ?? '').slice(0, 10);
  const t = Date.parse(until);
  if (!Number.isFinite(t)) return null;
  if (t < Date.parse(new Date().toISOString().slice(0, 10))) return null;
  return { ...(s as TaskSnooze), until };
}

export function isSnoozed(task: Parameters<typeof taskSnooze>[0]): boolean {
  return taskSnooze(task) !== null;
}

/**
 * Tasks whose completion is a MACHINE FACT, not a human judgement: WF37 closes them itself when
 * CMS accepts the submission (`completion_source: 'system'`). A hand-completion would assert that
 * CMS holds a report it has never received — and for the baseline it does more than mislead the
 * board: a completed `rpt_baseline_submission` is what LOCKS WF9's rolling BP baseline and reveals
 * it to the patient as official (S-23/S-30). Ticking the box by hand freezes a baseline CMS never
 * got.
 *
 * ⚠️ Deliberately NOT every `rpt_*`. `rpt_baseline_completeness_check` stays hand-completable even though
 * WF79 (passing preview) and WF37 (accepted filing) close it — a hand tick asserts nothing CMS holds. Cancelling stays available
 * throughout — deciding NOT to file is a human call.
 */
const SYSTEM_COMPLETED = /_submission$/;

// WF67 closes a withdrawal request when the withdrawal lands; a hand tick would hide a patient
// CMS still holds. The literal mirrors logic/withdrawalRequest.ts, which imports this module.
const WITHDRAWAL_REQUEST = 'cc_unalign_after_lockin';

export function isSystemCompletedTask(templateCode?: string | null): boolean {
  if (templateCode === WITHDRAWAL_REQUEST) return true;
  return !!templateCode && /^rpt_/.test(templateCode) && SYSTEM_COMPLETED.test(templateCode);
}

// Mirrors WF17's cap. WF17 clamps silently, so the modal must use the same number — S-93 [C3c].
export const SNOOZE_MAX_DAYS = 30;

// A billing deadline caps the snooze at its own due_at (WF17 clamps server-side; the modal
// disables the presets so the limit is visible BEFORE the click).
const DEADLINE_BOUND = /^(rpt_|rec_monthly_claims$|rec_billing_blocked_baseline$)/;
type TaskRef = NonNullable<Parameters<typeof templateCodeOf>[0]>;
export function snoozeCeiling(
  task: (TaskRef & { due_at?: string | null }) | null | undefined,
): string | null {
  if (!task || !DEADLINE_BOUND.test(templateCodeOf(task))) return null;
  const d = String(task.due_at ?? '').slice(0, 10);
  return Number.isFinite(Date.parse(d)) ? d : null;
}

export function isSnoozable(task: Parameters<typeof templateCodeOf>[0]): boolean {
  return !templateCodeOf(task).startsWith('exc_');
}

// The patient file holds its tasks in LOCAL state (usePatientFileData.bundledTasks), not in the
// TanStack cache under TASK_KEYS — so a snooze written through the mutation's cache patch never
// reaches it and the row sits unchanged until a manual page refresh. These mirror
// applyOptimisticCompletion: patch the bundle now, then pollTasksUntil reconciles when WF17 lands.
export function applyOptimisticSnooze<T extends TaskLike>(
  bundle: T[] | null | undefined,
  taskId: string | number,
  until: string,
  reason: string,
  actor?: CompletionActor,
): T[] {
  const idStr = String(taskId);
  return (bundle || []).map((t) => (String(t.task_id) === idStr
    ? {
      ...t,
      context_json: {
        ...(readTaskContext(t.context_json) || {}),
        snooze: {
          until,
          reason,
          by: actor
            ? { user_id: actor.user_id, firstname: actor.firstname, lastname: actor.lastname }
            : null,
          at: new Date().toISOString(),
        },
      },
    }
    : t));
}

export function applyOptimisticResume<T extends TaskLike>(
  bundle: T[] | null | undefined,
  taskId: string | number,
): T[] {
  const idStr = String(taskId);
  return (bundle || []).map((t) => {
    if (String(t.task_id) !== idStr) return t;
    const ctx = { ...(readTaskContext(t.context_json) || {}) };
    delete ctx.snooze;
    return { ...t, context_json: ctx };
  });
}

interface TaskAppointment {
  /** null when the stamp carries no parseable datetime. */
  scheduledAt: Date | null;
  /** The join key, private to this module: it tells two appointments at the same time apart. */
  apptId: string | null;
  kind: VisitKind | null;
  state: 'booked' | 'done' | 'cancelled' | 'missed';
  /** The live Elation status word at the last sync ("Scheduled", "Checked In", "With Doctor").
   *  On a passed appointment it is the difference between "the patient never arrived" and "the
   *  visit happened and nobody closed it" — the card cannot tell those apart without it. */
  elationStatus: string | null;
}

const APPOINTMENT_STATES = new Set(['booked', 'done', 'cancelled', 'missed']);

// Reads WF69's Elation stamp. Fails OPEN like taskSnooze — an unknown state degrades to the
// card's normal rendering rather than a guess. ehr_appointment_id is never returned: join key.
function taskAppointment(
  task: { context_json?: unknown } | null | undefined,
): TaskAppointment | null {
  const a = readTaskContext(task?.context_json)?.appointment;
  if (!a || typeof a !== 'object' || Array.isArray(a)) return null;
  const raw = a as { state?: unknown; scheduled_at?: unknown; elation_status?: unknown; ehr_appointment_id?: unknown };
  const state = String(raw.state ?? '');
  if (!APPOINTMENT_STATES.has(state)) return null;
  const t = Date.parse(String(raw.scheduled_at ?? ''));
  // Absent on stamps written before the field existed (WF69 backfills them on its next tick) —
  // every consumer must read it as "not known yet", never as a status.
  const es = typeof raw.elation_status === 'string' ? raw.elation_status.trim() : '';
  const id = raw.ehr_appointment_id == null ? '' : String(raw.ehr_appointment_id).trim();
  return {
    scheduledAt: Number.isFinite(t) ? new Date(t) : null,
    apptId: id || null,
    kind: visitKindOf(a),
    state: state as TaskAppointment['state'],
    elationStatus: es || null,
  };
}

/** "Aug 25, 7:00 PM" — the viewer's own zone, so the time never needs re-interpreting. */
function formatAppointmentAt(at: Date): string {
  return at.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Long form for the tooltip. Names the zone — a bare clock time is the DOB-off-by-one shape.
function formatAppointmentLong(at: Date): string {
  return at.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export interface AppointmentPresentation {
  state: 'booked' | 'passed_unconfirmed' | 'done' | 'cancelled' | 'missed';
  /** WF69's last-seen Elation status word, or null on a stamp that predates the field. */
  elationStatus: string | null;
  /** cc | np from the stamp; null names no kind (never default to NP: old stamps include Welcome Calls). */
  kind: VisitKind | null;
  /** Replaces the caller's own primary line; null = keep it (the date it shows is still right). */
  label: string | null;
  /** Muted sub-line naming where the fact came from. */
  origin: string;
  tone: string;
  at: Date | null;
}

// `passed_unconfirmed` is load-bearing: WF69 refuses to INFER a no-show, so neither may the UI.
export function appointmentPresentation(
  task: { context_json?: unknown } | null | undefined,
  now: Date = new Date(),
): AppointmentPresentation | null {
  const appt = taskAppointment(task);
  if (!appt) return null;
  if (appt.state === 'done') {
    return {
      state: 'done',
      elationStatus: appt.elationStatus,
      kind: appt.kind,
      label: null,
      origin: 'checked out in Elation',
      tone: 'var(--wi-text-primary-good)',
      at: appt.scheduledAt,
    };
  }
  if (appt.state === 'cancelled') {
    return {
      state: 'cancelled',
      elationStatus: appt.elationStatus,
      kind: appt.kind,
      label: null,
      origin: 'Previous booking cancelled in Elation',
      tone: 'var(--wi-text-secondary)',
      at: appt.scheduledAt,
    };
  }
  if (appt.state === 'missed') {
    // label stays null, like done/cancelled: the task's own due date is the useful primary line
    // (WF69 restores it), and replacing it would hide both the deadline and the Overdue word.
    return {
      state: 'missed',
      elationStatus: appt.elationStatus,
      kind: appt.kind,
      label: null,
      origin: appt.scheduledAt
        ? `Recorded as a no-show in Elation · ${formatAppointmentAt(appt.scheduledAt)}`
        : 'Recorded as a no-show in Elation',
      tone: 'var(--wi-status-bad)',
      at: appt.scheduledAt,
    };
  }
  // booked — without a datetime there is no booking worth announcing.
  const at = appt.scheduledAt;
  if (!at) return null;
  const passed = now.getTime() - at.getTime() > 0;
  return {
    state: passed ? 'passed_unconfirmed' : 'booked',
    elationStatus: appt.elationStatus,
    kind: appt.kind,
    label: passed
      ? `Visit time passed · ${formatAppointmentAt(at)}`
      : `Booked · ${formatAppointmentAt(at)}`,
    origin: passed
      ? `Not checked out in Elation${appt.elationStatus ? ` · still “${appt.elationStatus}”` : ''}`
      : 'from Elation',
    // Attention, never alarm: --wi-status-bad stays reserved for genuine overdue, or the one
    // truly-late state loses its meaning.
    tone: 'var(--wi-text-primary-moderate)',
    at,
  };
}

/** True while an appointment stamp explains the date on the card — the caller must then NOT
 *  render its own "Overdue", because a booked slot in the past is ambiguous, not late.
 *  INVARIANT for any state added here: `label !== null` implies `hasAppointmentState === true`.
 *  A state that replaces the card's primary line while the caller still renders its own lateness
 *  shows two verdicts at once; `missed` keeps a null label for exactly this reason. */
export function hasAppointmentState(
  task: { context_json?: unknown } | null | undefined,
  now?: Date,
): boolean {
  const p = appointmentPresentation(task, now);
  // 'done' counts too: a reopened auto-completion still has the check-out explaining the date.
  // 'missed' joins 'cancelled': a recorded no-show doesn't explain the date away, it IS late.
  return p !== null && p.state !== 'cancelled' && p.state !== 'missed';
}

// The single source of "this task is late" — it was computed independently in five renderers.
// False whenever an Elation appointment explains the date: unresolved is not the same as late.
export function isTaskOverdue(
  task: { status?: string; due_at?: string | null; context_json?: unknown } | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!task) return false;
  if (task.status && CLOSED_STATUSES.has(task.status)) return false;
  if (hasAppointmentState(task, now)) return false;
  const due = task.due_at ? Date.parse(task.due_at) : NaN;
  return Number.isFinite(due) && due < now.getTime();
}

export interface UpcomingVisit {
  at: Date;
  bucket: 'today' | 'tomorrow' | 'upcoming' | 'passed';
  label: string;
  /** For the condensed topbar, where the full sentence would crowd out the patient's name.
   *  Still self-sufficient: never a bare time or date that could read as any day. */
  compactLabel: string;
  hint: string;
  taskId: string | number | undefined;
}

const DAY_BUCKET: Record<number, 'today' | 'tomorrow'> = { 0: 'today', 1: 'tomorrow' };
const VISIT_PASSED_DAYS = 14;

const startOfLocalDay = (d: Date): number => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

// Whole calendar days between two instants, local time. Rounded because a DST boundary makes the
// raw difference a non-multiple of 24h — truncating there turns "tomorrow" into "today".
export const localDayDelta = (from: Date, to: Date): number => Math.round(
  (startOfLocalDay(to) - startOfLocalDay(from)) / 86400000,
);

// The patient's next Elation visit. Prefers the soonest future booking however far out, else a
// recently-passed unreconciled one (windowed: past that, it is a task to reconcile). Returning
// null means "nothing recorded" — never render it as "no upcoming visit".
export function upcomingVisit(
  tasks: readonly ({ task_id?: string | number; context_json?: unknown })[] | null | undefined,
  now: Date = new Date(),
): UpcomingVisit | null {
  const presented = (tasks || []).map((t) => ({
    task: t, pres: appointmentPresentation(t, now), apptId: taskAppointment(t)?.apptId ?? null,
  }));
  // A patient's staff and patient cards stamp the SAME appointment, and a sibling may already say
  // no-show. Matched by appointment id, so a missed visit never hides another one booked at the same
  // time; by time only when either stamp predates the id.
  const missed = presented.filter((c) => c.pres && c.pres.state === 'missed');
  const missedIds = new Set(missed.map((c) => c.apptId).filter((x): x is string => !!x));
  const missedAtAll = new Set(missed.filter((c) => c.pres!.at).map((c) => c.pres!.at!.getTime()));
  const missedAtNoId = new Set(missed.filter((c) => !c.apptId && c.pres!.at).map((c) => c.pres!.at!.getTime()));
  const isMissed = (apptId: string | null, at: Date): boolean => (apptId
    ? missedIds.has(apptId) || missedAtNoId.has(at.getTime())
    : missedAtAll.has(at.getTime()));
  const candidates = presented
    .filter((c): c is { task: typeof c.task; pres: AppointmentPresentation & { at: Date }; apptId: string | null } => (
      !!c.pres && !!c.pres.at
      && (c.pres.state === 'booked' || c.pres.state === 'passed_unconfirmed')
      && !isMissed(c.apptId, c.pres.at)
    ));
  if (!candidates.length) return null;

  const future = candidates
    .filter((c) => c.pres.state === 'booked')
    .sort((a, b) => a.pres.at.getTime() - b.pres.at.getTime())[0];
  const chosen = future ?? candidates
    .filter((c) => c.pres.state === 'passed_unconfirmed'
      && -localDayDelta(now, c.pres.at) <= VISIT_PASSED_DAYS)
    .sort((a, b) => b.pres.at.getTime() - a.pres.at.getTime())[0];
  if (!chosen) return null;

  const { at, kind } = chosen.pres;
  const time = at.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
  const long = formatAppointmentLong(at);
  const visitWord = kind ? `${VISIT_KIND_SHORT[kind].charAt(0).toUpperCase()}${VISIT_KIND_SHORT[kind].slice(1)}` : 'Visit';
  if (chosen.pres.state === 'passed_unconfirmed') {
    return {
      at,
      bucket: 'passed',
      label: `${visitWord} time passed · ${formatAppointmentAt(at)}`,
      // "Passed" carries the meaning without the tone having to: an amber date alone would be
      // indistinguishable from an upcoming one.
      compactLabel: `Passed ${at.toLocaleString('en-US', { month: 'short', day: 'numeric' })}`,
      // Never "missed"/"no-show"/"late": whether it happened is genuinely unknown here.
      hint: `The Elation appointment${kind ? ` (${VISIT_KIND_NAME[kind]} with the ${VISIT_KIND_PERSON[kind]})` : ''} for ${long} has passed and has not been checked out in Elation.${
        chosen.pres.elationStatus ? ` It still shows as “${chosen.pres.elationStatus}” there.` : ''
      } Whether the visit took place is unknown — check the chart.`,
      taskId: chosen.task.task_id,
    };
  }
  const delta = localDayDelta(now, at);
  // bucket and the displayed word coincide for today/tomorrow; anything else gets the weekday.
  const bucket = DAY_BUCKET[delta] ?? 'upcoming';
  const when = DAY_BUCKET[delta]
    // Weekday + date, not "in 5 days" — someone scheduling around it needs the weekday.
    ?? at.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const dayWord = DAY_BUCKET[delta];
  return {
    at,
    bucket,
    label: `${visitWord} ${when} · ${time}`,
    compactLabel: dayWord
      ? `${dayWord.charAt(0).toUpperCase()}${dayWord.slice(1)} ${time}`
      : at.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    hint: `${kind ? `${VISIT_KIND_NAME[kind]} with the ${VISIT_KIND_PERSON[kind]}` : 'Virtual visit'} booked in Elation for ${long}. Recorded from the Elation appointment; the back-office is not live-connected to it.`,
    taskId: chosen.task.task_id,
  };
}
